import { useWorkspaceText } from '../interface-copy';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPresentationCopy from '@/i18n/ns/agent-presentation';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import workspaceFeedbackCopy from '@/i18n/ns/workspace-feedback';
import { overlaysCopy, type OverlaysCopyKey } from '@/i18n/ns/overlays';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { CanvasFooter } from '../canvas-footer';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { agentsApi, type Agent } from './api';
import { makingNewAgent, withoutNewAgent } from './new-agent';
import { describeSchedule, turnOnQuestion } from './schedule';
import type { Act } from './shared';
import { nextRunPhrase } from './time';

// The agent canvas's one footer, the same on every tab (agent redesign
// 2026-10-09): one sentence of where the agent stands, and the one or two
// controls that change it. A draft is tested and created here; a created
// agent is paused or turned on; a reader copies it or asks to edit it.

/** A personal agent runs as its owner with her private connections, so she is told when the
 * live version is one someone else wrote (for example before an admin made the company agent
 * personal). */
export function liveAuthorWarning(
  agent: Agent,
  copy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>> = agentPresentationCopy.en,
) {
  const author = agent.published?.author;
  if (agent.scope !== 'personal' || !agent.isOwner || !author) return null;
  if (author.userId === agent.owner?.userId) return null;
  return formatCopy(copy.liveAuthor, {
    revision: agent.published!.revision,
    name: author.name ?? copy.formerMember,
  });
}

/** Where a created agent stands, in one sentence and one line under it. */
function statusOf(
  agent: Agent,
  copy: Readonly<Record<OverlaysCopyKey, string>>,
  agentCopy: Readonly<Record<keyof typeof agentPresentationCopy.en, string>>,
  locale: string,
  runtime: Readonly<Record<keyof typeof runtimeCopy.en, string>>,
): { sentence: string; detail: string } {
  const edit = agent.access.edit;
  const trigger = agent.trigger;
  if (!agent.publishedRevision)
    return { sentence: agentCopy.notCreated, detail: agentCopy.runsWhenCreated };
  if (!trigger)
    return {
      sentence: agentCopy.onDemand,
      detail: edit ? agentCopy.onDemandEdit : agentCopy.onDemandRun,
    };
  if (agent.enabled)
    return {
      sentence: agent.nextFireAt
        ? formatCopy(agentCopy.nextRun, {
            date: nextRunPhrase(agent.nextFireAt, new Date(), locale, runtime),
          })
        : agentCopy.on,
      detail: edit ? agentCopy.pausing : agentCopy.alsoRun,
    };
  return {
    sentence: formatCopy(agentCopy.off, {
      schedule: describeSchedule(trigger.schedule, copy, locale),
    }),
    detail: agentCopy.offRun,
  };
}

function Status({ sentence, detail }: { sentence: string; detail: string }) {
  return (
    <div className="ws-agents-footer-status">
      <strong data-testid="agent-status">{sentence}</strong>
      {detail && <p>{detail}</p>}
    </div>
  );
}

/** Test run tries the draft once. Create publishes it and switches its saved schedule on. */
function DraftFooter({ agent, busy, act }: { agent: Agent; busy: boolean; act: Act }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const ws = useWorkspace();
  const trigger = agent.trigger;
  // A part changed but not saved yet would not be what runs: save or cancel it first.
  const unsaved = ws.canvasDirty;
  const reason = unsaved ? agentCopy.saveCancel : undefined;
  const testRun = async () => {
    if (unsaved) return;
    const ownsNavigation = ws.beginNavigation();
    await act(async () => {
      const started = await agentsApi.tryDraft(agent.id);
      if (ownsNavigation()) ws.openThread(started.threadId);
    }, agentCopy.testChat);
  };
  const create = () =>
    !unsaved &&
    act(
      async () => {
        await agentsApi.publish(agent.id, {
          revision: agent.currentRevision,
          note: 'Created agent',
        });
        if (trigger)
          await agentsApi.setTrigger(agent.id, {
            schedule: { cron: trigger.schedule.cron, timezone: trigger.schedule.timezone },
            enabled: true,
          });
        // The chat that made it is ordinary chat again.
        if (makingNewAgent(ws.turnContext)) ws.setTurnContext(withoutNewAgent(ws.turnContext));
      },
      trigger
        ? formatCopy(feedback.agentCreatedOn, { name: agent.name })
        : formatCopy(feedback.agentCreatedReady, { name: agent.name }),
    );
  return (
    <>
      <Status sentence={agentCopy.draftAuto} detail={reason ?? agentCopy.testFirst} />
      <span className="ws-actions">
        <button
          className="ws-button"
          disabled={busy}
          aria-disabled={unsaved}
          title={reason}
          onClick={() => void testRun()}
        >
          {uiRich('{value1} Test run', { value1: <WorkspaceIcon name="play" size={14} /> })}
        </button>
        <button
          className="ws-button primary"
          disabled={busy}
          aria-disabled={unsaved}
          title={reason}
          onClick={() => void create()}
        >
          {trigger ? uiText('Create and turn on') : uiText('Create')}
        </button>
      </span>
    </>
  );
}

/** Copy, and (company agents) ask for edit access: what a reader can do besides Run now. */
function ReaderActions({ agent, busy, act }: { agent: Agent; busy: boolean; act: Act }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const ws = useWorkspace();
  const copy = () =>
    act(async () => {
      const ownsNavigation = ws.beginNavigation();
      const copied = await agentsApi.copy(agent.id);
      if (ownsNavigation()) ws.openCanvas({ kind: 'agent', id: copied.id });
    }, agentCopy.copied);
  return (
    <span className="ws-actions">
      {agent.scope === 'company' &&
        (agent.myEditAccess === 'requested' ? (
          <button className="ws-button" disabled>
            {uiText('Edit access requested')}
          </button>
        ) : (
          <button
            className="ws-button"
            disabled={busy}
            onClick={() =>
              void act(
                () => agentsApi.requestEditAccess(agent.id),
                feedback.agentEditAccessRequested,
              )
            }
          >
            {uiRich('{value1} Request edit access', {
              value1: <WorkspaceIcon name="key" size={14} />,
            })}
          </button>
        ))}
      <button className="ws-button" disabled={busy} onClick={() => void copy()}>
        {uiRich('{value1} Copy agent', { value1: <WorkspaceIcon name="copy" size={14} /> })}
      </button>
    </span>
  );
}

export function AgentFooter({
  agent,
  busy,
  act,
  draft,
  hidden,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  /** An agent never created that this person edits: Test run and Create. */
  draft: boolean;
  hidden: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(overlaysCopy);
  const agentCopy = useNamespaceCopy(agentPresentationCopy);
  const runtime = useNamespaceCopy(runtimeCopy);
  const locale = useChatInterfaceLocale();
  const [confirmingOn, setConfirmingOn] = useState(false);
  const trigger = agent.trigger;
  const edit = agent.access.edit;
  if (draft)
    return (
      <CanvasFooter className="ws-agents-footer" hidden={hidden}>
        <DraftFooter agent={agent} busy={busy} act={act} />
      </CanvasFooter>
    );
  // Turn on is the saved schedule switched on: the trigger write, after a confirmation that
  // names the schedule. The server rechecks publication and authority.
  const turnOn = () => {
    if (!trigger) return;
    setConfirmingOn(false);
    void act(
      () =>
        agentsApi.setTrigger(agent.id, {
          schedule: { cron: trigger.schedule.cron, timezone: trigger.schedule.timezone },
          enabled: true,
        }),
      copy.agentSwitchedOn,
    );
  };
  const pause = () =>
    void act(() => agentsApi.patch(agent.id, { enabled: false }), agentCopy.offRun);
  const scheduled = Boolean(agent.publishedRevision && trigger);
  const control = !scheduled ? null : agent.enabled ? (
    <button className="ws-button" disabled={busy} onClick={pause}>
      {uiText('Pause')}
    </button>
  ) : (
    <button
      className="ws-button primary"
      data-testid="agent-turn-on"
      disabled={busy}
      onClick={() => setConfirmingOn(true)}
    >
      {copy.agentTurnOn}
    </button>
  );
  return (
    <CanvasFooter className="ws-agents-footer" hidden={hidden}>
      <Status {...statusOf(agent, copy, agentCopy, locale, runtime)} />
      {edit
        ? control && <span className="ws-actions">{control}</span>
        : agent.publishedRevision && <ReaderActions agent={agent} busy={busy} act={act} />}
      {confirmingOn && trigger && (
        <ConfirmDialog
          title={agent.name}
          description={[
            turnOnQuestion(trigger.schedule, copy, locale),
            liveAuthorWarning(agent, agentCopy),
          ]
            .filter(Boolean)
            .join(' ')}
          confirmLabel={copy.agentTurnOn}
          tone="default"
          busy={busy}
          onCancel={() => setConfirmingOn(false)}
          onConfirm={turnOn}
        />
      )}
    </CanvasFooter>
  );
}
